import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwbG9naXN0aWNzfGVufDB8fHx8MTc4NTYxMzQ3N3ww&ixlib=rb-4.1.0&q=85";

const TIERS = [
  { label: "1 store", price: "9.99", desc: "Perfect for a single branch getting started." },
  { label: "2–3 stores", price: "14.99", desc: "For small courier operations." },
  { label: "4–6 stores", price: "20.99", desc: "For growing regional chains." },
  { label: "7–9 stores", price: "24.99", desc: "For established multi-branch networks." },
  { label: "10–15 stores", price: "49.99", desc: "For large operations. Best value.", featured: true },
];

const FEATURES = [
  "Real-time status boards & live wait timers",
  "SLA alerts & overdue accountability",
  "Supervisor & admin dashboards",
  "Reports, exports & email digests",
  "Kiosk check-in for every store",
  "Unlimited staff logins",
];

export default function Pricing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen relative">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${BG})` }} />
      <div className="absolute inset-0 bg-black/90" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-14">
        <div className="text-center mb-10">
          <div className="inline-flex bg-white rounded-lg px-5 py-3 mb-6">
            <img src="/trackboard-logo.png" alt="Trackboard by LOM Group JA" className="w-48 h-auto" data-testid="pricing-logo" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mb-3">Simple pricing, per company</h1>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            One monthly price based on how many stores you run. Every feature included — no per-seat fees.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          {TIERS.map((t) => (
            <div
              key={t.label}
              data-testid={`pricing-tier-${t.price}`}
              className={`bg-card border p-5 flex flex-col ${t.featured ? "border-alert-green shadow-[0_0_0_1px_hsl(var(--alert-green))]" : "border-border"}`}
            >
              {t.featured && <div className="text-xs font-semibold text-alert-green uppercase tracking-wide mb-2">Best value</div>}
              <div className="text-xs text-muted-foreground uppercase tracking-wide">{t.label}</div>
              <div className="font-display text-3xl font-extrabold mt-1">
                ${t.price}<span className="text-sm font-normal text-muted-foreground">/mo</span>
              </div>
              <p className="text-sm text-muted-foreground mt-3 flex-1">{t.desc}</p>
            </div>
          ))}
        </div>

        <div className="bg-card border border-border p-6 max-w-3xl mx-auto mb-10">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-4">Everything included in every plan</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FEATURES.map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm">
                <Check size={16} className="text-alert-green shrink-0" /> {f}
              </div>
            ))}
          </div>
        </div>

        <div className="text-center flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate("/register")} className="h-11 px-8 text-base font-semibold" data-testid="pricing-get-started">
            Get started
          </Button>
          <Button variant="outline" onClick={() => navigate("/login")} className="h-11 px-8 text-base" data-testid="pricing-sign-in">
            Sign in
          </Button>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">Prices in USD. New accounts are reviewed and approved before activation.</p>
      </div>
    </div>
  );
}
